// Job pipeline W2 (2026-09-06), §3.2/3.5: `analyze_scene`'s volume/velocity
// viz layers arrive as artifact references (`{ field: ArtifactRef, inside:
// ArtifactRef }`) instead of the legacy inline `field_b64`/`inside_b64`.
// This is the ONE adapter — shared by `packVolumetricCloud.ts` and
// `sliceVolumetricSweep.ts` — that turns either shape into decoded bytes.
// Neither file should hand-roll a second decode path for the ref shape.
//
// `b64ToBytes` lives here (not duplicated per-file) for the same reason:
// both consumers are already in `web/`, so there is no cross-environment
// constraint forcing separate copies the way there is between `web/` and
// `packages/acoustics-client` (see that package's own
// `resolveVolumeBytes` — a deliberate, documented duplicate because it
// must not assume a DOM `atob`).
import { isArtifactRef, type ArtifactRef } from '@/lib/adminTypes';

export function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Inverse of `b64ToBytes` — used to synthesize a legacy inline
 *  (`field_b64`/`inside_b64`) response from freshly-decoded artifact bytes
 *  so `useModalViz`'s existing per-layer caches (and every downstream
 *  consumer that expects that shape, e.g. `packVolumetricCloud`) keep
 *  working unchanged. Chunked to avoid blowing the call stack on a large
 *  cube (`String.fromCharCode(...bytes)` with a spread would pass the
 *  whole array as arguments). */
export function bytesToB64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

export interface DecodedVolumeBytes {
  field: Uint8Array;
  inside: Uint8Array;
}

export type FetchArtifactFn = (sha: string, signal?: AbortSignal) => Promise<Uint8Array>;

/** Legacy inline responses resolve synchronously (still wrapped in a
 *  Promise for a uniform call site); ref-form responses await
 *  `fetchArtifact` for both cubes in parallel. */
export function resolveVolumeBytes(
  resp:
    | { field_b64: string; inside_b64: string }
    | { field: ArtifactRef; inside: ArtifactRef },
  fetchArtifact: FetchArtifactFn,
): Promise<DecodedVolumeBytes> {
  if ('field_b64' in resp) {
    return Promise.resolve({
      field: b64ToBytes(resp.field_b64),
      inside: b64ToBytes(resp.inside_b64),
    });
  }
  return Promise.all([
    fetchArtifact(resp.field.artifact),
    fetchArtifact(resp.inside.artifact),
  ]).then(([field, inside]) => ({ field, inside }));
}

/** The two wire forms of a volume-shaped response (pressure sweep or
 *  velocity): the legacy inline base64 cube, and the artifact-referenced
 *  one the job pipeline emits since W2/W5. */
export interface VolumeInline { field_b64: string; inside_b64: string }
export interface VolumeRef { field: ArtifactRef; inside: ArtifactRef }

/** `true` for the artifact-referenced form, detected POSITIVELY on
 *  `field` being an `ArtifactRef` (same rule as `isRaytraceRef` /
 *  `isModalRef`). Anything else -- the inline form, or a shape this code
 *  doesn't know -- is left alone and handled as inline by the caller. */
export function isVolumeRef(v: object): v is VolumeRef {
  return isArtifactRef((v as { field?: unknown }).field);
}

/** Resolve a volume-shaped response to the INLINE form the module caches
 *  (`volumetricCache` / `velocityCache`) and the synchronous packers
 *  (`packVolumetricCloud` / `packVolumetricVelocity`) are typed for.
 *  An already-inline value resolves to itself, same reference, no fetch.
 *
 *  This is the ONE place a ref-form cube becomes an inline one. Every
 *  entry point that lands a cube in a cache -- `useModalViz`'s
 *  analysis.viz primer and `primeTemplateViz` for template / placement-
 *  candidate viz -- goes through it. A ref-form object written into a
 *  cache directly reaches `packVolumetricCloud` inside a render
 *  (`atob(undefined)`) and takes the whole page down through the error
 *  boundary; that is exactly how placement results crashed after the
 *  placement job switched its candidate cubes to artifact refs.
 *
 *  `Inline` and `Ref` are the paired wire types for one layer
 *  (`ModalVolumeSweepResponse` / `VolumeSweepRefResponse`, or the
 *  velocity pair); they share their metadata by construction, so the
 *  resolved value IS an `Inline`. */
export async function inlineVolumeResponse<
  Inline extends VolumeInline,
  Ref extends VolumeRef,
>(v: Inline | Ref, fetchArtifact: FetchArtifactFn): Promise<Inline> {
  if (!isVolumeRef(v)) return v as Inline;
  const { field, inside } = await resolveVolumeBytes(v, fetchArtifact);
  const { field: _field, inside: _inside, ...meta } = v;
  return {
    ...meta, field_b64: bytesToB64(field), inside_b64: bytesToB64(inside),
  } as unknown as Inline;
}
